import React from 'react';

export const PrivacyPolicyContent = () => (
  <div className="prose prose-sm max-w-none">
    <p className="text-gray-600 mb-4"><strong>Last Updated:</strong> February 2, 2026</p>
    
    <h3 className="text-xl font-semibold text-navy mt-6 mb-3">1. Introduction</h3>
    <p className="text-gray-700 mb-4">
      Elvex Tech Services ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website.
    </p>

    <h3 className="text-xl font-semibold text-navy mt-6 mb-3">2. Information We Collect</h3>
    <p className="text-gray-700 mb-2">We may collect information about you in various ways, including:</p>
    <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
      <li><strong>Personal Data:</strong> Name, email address, company name, and other contact information you provide through our contact form.</li>
      <li><strong>Usage Data:</strong> Information about how you access and use our website, including your IP address, browser type, pages visited, and time spent on pages.</li>
      <li><strong>Cookies:</strong> We use cookies and similar tracking technologies to track activity on our website. See our Cookie Policy for more details.</li>
    </ul>

    <h3 className="text-xl font-semibold text-navy mt-6 mb-3">3. How We Use Your Information</h3>
    <p className="text-gray-700 mb-2">We use the information we collect for various purposes:</p>
    <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
      <li>To respond to your inquiries and provide customer service</li>
      <li>To send you marketing communications (with your consent)</li>
      <li>To improve our website and services</li>
      <li>To analyze website usage and trends</li>
      <li>To comply with legal obligations</li>
    </ul>

    <h3 className="text-xl font-semibold text-navy mt-6 mb-3">4. Disclosure of Your Information</h3>
    <p className="text-gray-700 mb-2">We may share your information in the following situations:</p>
    <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
      <li><strong>Service Providers:</strong> With third-party service providers who perform services on our behalf</li>
      <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
      <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
    </ul>

    <h3 className="text-xl font-semibold text-navy mt-6 mb-3">5. Your Data Protection Rights (GDPR)</h3>
    <p className="text-gray-700 mb-2">If you are a resident of the European Economic Area (EEA), you have certain data protection rights:</p>
    <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
      <li><strong>Right to Access:</strong> Request copies of your personal data</li>
      <li><strong>Right to Rectification:</strong> Request correction of inaccurate data</li>
      <li><strong>Right to Erasure:</strong> Request deletion of your personal data</li>
      <li><strong>Right to Restrict Processing:</strong> Request restriction of processing your data</li>
      <li><strong>Right to Data Portability:</strong> Request transfer of your data</li>
      <li><strong>Right to Object:</strong> Object to our processing of your data</li>
    </ul>

    <h3 className="text-xl font-semibold text-navy mt-6 mb-3">6. Data Security</h3>
    <p className="text-gray-700 mb-4">
      We implement appropriate technical and organizational security measures to protect your personal information. However, no method of transmission over the Internet is 100% secure.
    </p>

    <h3 className="text-xl font-semibold text-navy mt-6 mb-3">7. International Data Transfers</h3>
    <p className="text-gray-700 mb-4">
      Your information may be transferred to and maintained on servers located outside of your country. We ensure appropriate safeguards are in place for such transfers.
    </p>

    <h3 className="text-xl font-semibold text-navy mt-6 mb-3">8. Children's Privacy</h3>
    <p className="text-gray-700 mb-4">
      Our website is not intended for children under 16 years of age. We do not knowingly collect personal information from children.
    </p>

    <h3 className="text-xl font-semibold text-navy mt-6 mb-3">9. Changes to This Privacy Policy</h3>
    <p className="text-gray-700 mb-4">
      We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
    </p>

    <h3 className="text-xl font-semibold text-navy mt-6 mb-3">10. Contact Us</h3>
    <p className="text-gray-700 mb-4">
      If you have any questions about this Privacy Policy, please contact us at:
    </p>
    <p className="text-gray-700 mb-2">
      <strong>Email:</strong> info@elvextech.com<br />
      <strong>Website:</strong> www.elvextech.com
    </p>
  </div>
);

export const CookiePolicyContent = () => (
  <div className="prose prose-sm max-w-none">
    <p className="text-gray-600 mb-4"><strong>Last Updated:</strong> February 2, 2026</p>
    
    <h3 className="text-xl font-semibold text-navy mt-6 mb-3">1. What Are Cookies?</h3>
    <p className="text-gray-700 mb-4">
      Cookies are small text files that are placed on your device when you visit our website. They help us provide you with a better experience by remembering your preferences and understanding how you use our site.
    </p>

    <h3 className="text-xl font-semibold text-navy mt-6 mb-3">2. Types of Cookies We Use</h3>
    
    <h4 className="text-lg font-semibold text-navy mt-4 mb-2">2.1 Essential Cookies</h4>
    <p className="text-gray-700 mb-4">
      These cookies are necessary for the website to function properly. They enable basic functions like page navigation and access to secure areas. The website cannot function properly without these cookies.
    </p>
    <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
      <li>Session management</li>
      <li>Security and authentication</li>
      <li>Cookie consent preferences</li>
    </ul>

    <h4 className="text-lg font-semibold text-navy mt-4 mb-2">2.2 Analytics Cookies</h4>
    <p className="text-gray-700 mb-4">
      These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve our website's performance and user experience.
    </p>
    <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
      <li>Page views and visit duration</li>
      <li>Traffic sources</li>
      <li>User navigation patterns</li>
    </ul>

    <h4 className="text-lg font-semibold text-navy mt-4 mb-2">2.3 Marketing Cookies</h4>
    <p className="text-gray-700 mb-4">
      These cookies track your online activity to help advertisers deliver more relevant advertising or to limit how many times you see an ad. We may share this information with other organizations, such as advertisers.
    </p>

    <h3 className="text-xl font-semibold text-navy mt-6 mb-3">3. Managing Cookies</h3>
    <p className="text-gray-700 mb-4">
      You can control and manage cookies in various ways. Please note that removing or blocking cookies can impact your user experience and some functionality may not work as intended.
    </p>

    <h4 className="text-lg font-semibold text-navy mt-4 mb-2">3.1 Cookie Consent Tool</h4>
    <p className="text-gray-700 mb-4">
      We provide a cookie consent banner when you first visit our website. You can customize your cookie preferences at any time by clicking the cookie settings link in our footer.
    </p>

    <h4 className="text-lg font-semibold text-navy mt-4 mb-2">3.2 Browser Settings</h4>
    <p className="text-gray-700 mb-4">
      Most browsers allow you to refuse or accept cookies. Here's how to manage cookies in popular browsers:
    </p>
    <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
      <li><strong>Chrome:</strong> Settings > Privacy and security > Cookies and other site data</li>
      <li><strong>Firefox:</strong> Options > Privacy & Security > Cookies and Site Data</li>
      <li><strong>Safari:</strong> Preferences > Privacy > Cookies and website data</li>
      <li><strong>Edge:</strong> Settings > Cookies and site permissions > Cookies and site data</li>
    </ul>

    <h3 className="text-xl font-semibold text-navy mt-6 mb-3">4. Third-Party Cookies</h3>
    <p className="text-gray-700 mb-4">
      Some cookies on our website may be set by third-party services that appear on our pages. We do not control these cookies. Please check the third-party websites for more information about their cookies.
    </p>

    <h3 className="text-xl font-semibold text-navy mt-6 mb-3">5. Updates to This Policy</h3>
    <p className="text-gray-700 mb-4">
      We may update this Cookie Policy from time to time. Any changes will be posted on this page with an updated revision date.
    </p>

    <h3 className="text-xl font-semibold text-navy mt-6 mb-3">6. Contact Us</h3>
    <p className="text-gray-700 mb-4">
      If you have any questions about our use of cookies, please contact us at info@elvextech.com
    </p>
  </div>
);

export const TermsOfServiceContent = () => (
  <div className="prose prose-sm max-w-none">
    <p className="text-gray-600 mb-4"><strong>Last Updated:</strong> February 2, 2026</p>
    
    <h3 className="text-xl font-semibold text-navy mt-6 mb-3">1. Agreement to Terms</h3>
    <p className="text-gray-700 mb-4">
      By accessing or using the Elvex Tech Services website, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using this site.
    </p>

    <h3 className="text-xl font-semibold text-navy mt-6 mb-3">2. Use License</h3>
    <p className="text-gray-700 mb-4">
      Permission is granted to temporarily access the materials on Elvex Tech Services' website for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title, and under this license you may not:
    </p>
    <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
      <li>Modify or copy the materials</li>
      <li>Use the materials for any commercial purpose or public display</li>
      <li>Attempt to decompile or reverse engineer any software on the website</li>
      <li>Remove any copyright or proprietary notations from the materials</li>
      <li>Transfer the materials to another person or "mirror" the materials on any other server</li>
    </ul>

    <h3 className="text-xl font-semibold text-navy mt-6 mb-3">3. Disclaimer</h3>
    <p className="text-gray-700 mb-4">
      The materials on Elvex Tech Services' website are provided on an 'as is' basis. Elvex Tech Services makes no warranties, expressed or implied, and hereby disclaims and negates all other warranties including, without limitation, implied warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement of intellectual property.
    </p>

    <h3 className="text-xl font-semibold text-navy mt-6 mb-3">4. Limitations</h3>
    <p className="text-gray-700 mb-4">
      In no event shall Elvex Tech Services or its suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use the materials on our website.
    </p>

    <h3 className="text-xl font-semibold text-navy mt-6 mb-3">5. Accuracy of Materials</h3>
    <p className="text-gray-700 mb-4">
      The materials appearing on Elvex Tech Services' website could include technical, typographical, or photographic errors. Elvex Tech Services does not warrant that any of the materials on its website are accurate, complete, or current.
    </p>

    <h3 className="text-xl font-semibold text-navy mt-6 mb-3">6. Links</h3>
    <p className="text-gray-700 mb-4">
      Elvex Tech Services has not reviewed all of the sites linked to its website and is not responsible for the contents of any such linked site. The inclusion of any link does not imply endorsement by Elvex Tech Services.
    </p>

    <h3 className="text-xl font-semibold text-navy mt-6 mb-3">7. Intellectual Property</h3>
    <p className="text-gray-700 mb-4">
      All content on this website, including but not limited to text, graphics, logos, images, and software, is the property of Elvex Tech Services or its content suppliers and is protected by international copyright laws.
    </p>

    <h3 className="text-xl font-semibold text-navy mt-6 mb-3">8. User Submissions</h3>
    <p className="text-gray-700 mb-4">
      By submitting content through our contact form or other means, you grant Elvex Tech Services a non-exclusive, royalty-free, perpetual, and worldwide license to use, reproduce, and publish such content for business purposes.
    </p>

    <h3 className="text-xl font-semibold text-navy mt-6 mb-3">9. Prohibited Uses</h3>
    <p className="text-gray-700 mb-2">You may not use our website:</p>
    <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
      <li>In any way that violates any applicable law or regulation</li>
      <li>To transmit any advertising or promotional material</li>
      <li>To impersonate or attempt to impersonate Elvex Tech Services or its employees</li>
      <li>To engage in any conduct that restricts or inhibits anyone's use of the website</li>
      <li>To introduce viruses, trojans, or other malicious code</li>
    </ul>

    <h3 className="text-xl font-semibold text-navy mt-6 mb-3">10. Termination</h3>
    <p className="text-gray-700 mb-4">
      We may terminate or suspend your access to our website immediately, without prior notice or liability, for any reason, including breach of these Terms of Service.
    </p>

    <h3 className="text-xl font-semibold text-navy mt-6 mb-3">11. Governing Law</h3>
    <p className="text-gray-700 mb-4">
      These terms shall be governed and construed in accordance with the laws of India, without regard to its conflict of law provisions.
    </p>

    <h3 className="text-xl font-semibold text-navy mt-6 mb-3">12. Changes to Terms</h3>
    <p className="text-gray-700 mb-4">
      We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting to the website. Your continued use of the website following the posting of changes constitutes acceptance of those changes.
    </p>

    <h3 className="text-xl font-semibold text-navy mt-6 mb-3">13. Contact Information</h3>
    <p className="text-gray-700 mb-4">
      If you have any questions about these Terms of Service, please contact us at:
    </p>
    <p className="text-gray-700 mb-2">
      <strong>Elvex Tech Services</strong><br />
      <strong>Email:</strong> info@elvextech.com<br />
      <strong>Website:</strong> www.elvextech.com
    </p>
  </div>
);
